import { Tables } from "@/lib/supabase/supabase_types_override";
import { QuestionConditionals } from "@/types/crm/configuration/configuration";
import { CommonProperties } from "@/types/utils";

export function replaceOldIdsWithNewIds<
  Q extends Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  > = Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  >
>({
  conditionals,
  questions,
}: {
  conditionals?: QuestionConditionals;
  questions: (Q & { newId: Q["id"] })[] | [];
}) {
  if (!conditionals || !Array.isArray(questions) || questions.length === 0) {
    return conditionals;
  }

  const oldIdToNewId = Object.fromEntries(
    questions.map((q) => [q.id, q.newId])
  );

  function transform(
    obj:
      | QuestionConditionals
      | QuestionConditionals[]
      | Record<string, QuestionConditionals>
      | undefined
  ): QuestionConditionals {
    if (Array.isArray(obj)) {
      return obj.map(transform);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
          if (oldIdToNewId[key]) {
            return [oldIdToNewId[key], transform(value)];
          }
          return [key, transform(value)];
        })
      );
    }
    return obj;
  }
  return transform(conditionals);
}

export function prepareQuestionsWithNewIds<
  Q extends Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  > = Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  >
>({ questions }: { questions?: Q[] }): Q[] {
  if (!questions) {
    throw new Error("Questions are required");
  }

  return (
    questions
      ?.map((question) => {
        return {
          ...question,
          // survey_id: surveyId,
          newId: self.crypto.randomUUID(), // this id will be used for conditionals mapping
        };
      })
      ?.map(({ newId, ...question }, _index, questionsArr) => {
        return {
          ...question,
          id: newId, // replace old id with new id
          selected_configuration: {
            ...question.selected_configuration,
            conditionals: replaceOldIdsWithNewIds({
              conditionals: question.selected_configuration?.conditionals,
              questions: questionsArr,
            }),
          },
        };
      }) || []
  );
}

export function prepareQuestionsWithNewIdsAndMapping<
  Q extends Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  > = Omit<
    CommonProperties<Tables<"question">, Tables<"survey_bank_question_bank">>,
    "created_at" | "survey_bank_id"
  >
>({ questions }: { questions?: Q[] }): {
  questions: Q[];
  idMapping: Record<string, string>;
} {
  if (!questions) {
    throw new Error("Questions are required");
  }

  // First pass: add newId to each question
  const questionsWithNewIds = questions.map((question) => {
    return {
      ...question,
      newId: self.crypto.randomUUID(), // this id will be used for conditionals mapping
    };
  });

  // Extract ID mapping (oldId -> newId)
  const idMapping = Object.fromEntries(
    questionsWithNewIds.map((q) => [q.id, q.newId])
  );

  // Second pass: replace old ids with new ids
  const transformedQuestions = questionsWithNewIds.map(({ newId, ...question }, _index, questionsArr) => {
    return {
      ...question,
      id: newId, // replace old id with new id
      selected_configuration: {
        ...question.selected_configuration,
        conditionals: replaceOldIdsWithNewIds({
          conditionals: question.selected_configuration?.conditionals,
          questions: questionsArr,
        }),
      },
    };
  });

  return {
    questions: transformedQuestions,
    idMapping,
  };
}
